<template>
  <v-row
    justify="center"
  >
    <v-dialog
      v-model="isVisible"
      :fullscreen="$vuetify.breakpoint.xsOnly"
      max-width="850"
      persistent
      scrollable
    >
      <v-card>
        <v-toolbar
          flat
          height="80"
          max-height="80"
        >
          <v-avatar
            :color="$t('Delivery.DeliveryStaffAddDialog.Toolbar.Color')"
          >
            <v-icon dark>
              {{ $t('Delivery.DeliveryStaffAddDialog.Toolbar.Icon') }}
            </v-icon>
          </v-avatar>

          <v-toolbar-title
            v-if="!deliveryStaffToEdit"
            class="pl-3"
          >
            <div class="body-3">
              {{ $t('Delivery.DeliveryStaffAddDialog.Toolbar.Add.Title') }}
            </div>
            <div class="text-caption">
              {{ $t('Delivery.DeliveryStaffAddDialog.Toolbar.Add.Subtitle') }}
            </div>
          </v-toolbar-title>

          <v-toolbar-title
            v-else
            class="pl-3"
          >
            <div class="body-3">
              {{ $t('Delivery.DeliveryStaffAddDialog.Toolbar.Edit.Title') }}
            </div>
            <div class="text-caption">
              {{ $t('Delivery.DeliveryStaffAddDialog.Toolbar.Edit.Subtitle') }}
            </div>
          </v-toolbar-title>

          <v-spacer />

          <v-btn
            icon
            @click="onCancelButtonClick"
          >
            <v-icon>close</v-icon>
          </v-btn>
        </v-toolbar>

        <v-divider />

        <v-card-text
          v-if="editDeliveryStaff"
          class="pa-4"
        >
          <v-form
            ref="deliveryStaffForm"
            v-model="deliveryStaffFormValid"
            @submit.prevent
          >
            <v-container
              class="pa-0"
              fluid
            >
              <template v-if="!deliveryStaffToEdit || (deliveryStaffToEdit && !editDeliveryStaff?.User?.EftPos)">
                <v-row>
                  <v-col
                    cols="12"
                    sm="6"
                  >
                    <v-text-field
                      v-model="editDeliveryStaff.Firstname"
                      :counter="32"
                      :hint="$t('Delivery.DeliveryStaffAddDialog.Field.Firstname.Hint')"
                      :label="$t('Delivery.DeliveryStaffAddDialog.Field.Firstname.Label')"
                      :prepend-icon="$t('Delivery.DeliveryStaffAddDialog.Field.Firstname.Icon')"
                      :rules="validationRules.deliveryStaffForm.Firstname"
                      class=""
                      required
                    />
                  </v-col>
                  <v-col
                    cols="12"
                    sm="6"
                  >
                    <v-text-field
                      v-model="editDeliveryStaff.Lastname"
                      :counter="32"
                      :hint="$t('Delivery.DeliveryStaffAddDialog.Field.Lastname.Hint')"
                      :label="$t('Delivery.DeliveryStaffAddDialog.Field.Lastname.Label')"
                      :prepend-icon="$t('Delivery.DeliveryStaffAddDialog.Field.Lastname.Icon')"
                      :rules="validationRules.deliveryStaffForm.Lastname"
                      class=""
                      required
                    />
                  </v-col>
                </v-row>

                <v-row>
                  <v-col
                    cols="12"
                    sm="6"
                  >
                    <v-text-field
                      v-model="editDeliveryStaff.Phone"
                      v-mask="'################'"
                      :hint="$t('Delivery.DeliveryStaffAddDialog.Field.Telephone.Hint')"
                      :label="$t('Delivery.DeliveryStaffAddDialog.Field.Telephone.Label')"
                      :prepend-icon="$t('Delivery.DeliveryStaffAddDialog.Field.Telephone.Icon')"
                      :rules="validationRules.deliveryStaffForm.Phone"
                      class=""
                      required
                    />
                  </v-col>
                  <v-col
                    cols="12"
                    sm="6"
                  >
                    <v-text-field
                      v-model="editDeliveryStaff.Email"
                      :counter="96"
                      :hint="$t('Delivery.DeliveryStaffAddDialog.Field.Email.Hint')"
                      :label="$t('Delivery.DeliveryStaffAddDialog.Field.Email.Label')"
                      :prepend-icon="$t('Delivery.DeliveryStaffAddDialog.Field.Email.Icon')"
                      :rules="validationRules.deliveryStaffForm.Email"
                      class=""
                    />
                  </v-col>
                </v-row>

                <v-row dense>
                  <v-col cols="12">
                    <v-textarea
                      v-model="editDeliveryStaff.Notes"
                      :hint="$t('Delivery.DeliveryStaffAddDialog.Field.Notes.Hint')"
                      :label="$t('Delivery.DeliveryStaffAddDialog.Field.Notes.Label')"
                      :prepend-icon="$t('Delivery.DeliveryStaffAddDialog.Field.Notes.Icon')"
                      auto-grow
                      rows="1"
                    />
                  </v-col>
                </v-row>
              </template>

              <v-row dense>
                <v-col cols="12">
                  <v-card flat>
                    <v-card-title :class="[isDarkMode ? 'grey darken-4' : 'grey lighten-4']">
                      <v-icon class="pr-3">
                        mdi-account-circle
                      </v-icon>
                      <h5 class="text-body-2 font-weight-bold">
                        {{ $tc('Staff.StaffGroups.Misc.User', 1) }}
                      </h5>

                      <v-spacer />

                      <v-switch
                        v-if="!deliveryStaffToEdit || (deliveryStaffToEdit && !editDeliveryStaff?.User?.EftPos)"
                        v-model="staffHasUser"
                        :label="staffHasUser ? $t('Common.Button.Yes') : $t('Common.Button.No')"
                        class="ma-0 pa-0"
                        color="primary"
                        hide-details
                        inset
                        @change="onUserEnabled"
                      />
                    </v-card-title>

                    <v-divider />

                    <v-alert
                      v-if="!staffHasUser && tmpUserData"
                      border="left"
                      class="mt-2 text-caption"
                      dense
                      type="warning"
                    >
                      {{ $t('Delivery.DeliveryUser.Warning') }}
                    </v-alert>

                    <v-card-text v-if="staffHasUser">
                      <v-container
                        class="pa-0"
                        fluid
                      >
                        <v-row dense>
                          <v-col
                            cols="12"
                            sm="6"
                          >
                            <v-text-field
                              v-model="editDeliveryStaff.User.Username"
                              :hint="$t('Waiter.StaffAddDialog.Field.Email.Hint')"
                              :label="$t('Waiter.StaffAddDialog.Field.Email.Label')"
                              :persistent-hint="!editDeliveryStaff.User.Username"
                              :prepend-icon="$t('Waiter.StaffAddDialog.Field.Email.Icon')"
                              class="pr-2 d-inline-flex"
                              readonly
                              style="width: 92%;"
                            />

                            <v-btn
                              :disabled="!editDeliveryStaff.User.Username"
                              class="ma-0"
                              icon
                              small
                              @click="copyToClipboard(editDeliveryStaff.User.Username)"
                            >
                              <v-icon small>
                                far fa-copy
                              </v-icon>
                            </v-btn>
                          </v-col>

                          <v-col
                            cols="12"
                            sm="6"
                          >
                            <v-text-field
                              v-model="editDeliveryStaff.User.Password"
                              v-mask="'####'"
                              :append-icon="!pinVisible ? 'visibility' : 'visibility_off'"
                              :counter="4"
                              :disabled="!userCanManage"
                              :hint="$t('Waiter.StaffAddDialog.Field.Pin.Hint')"
                              :label="$t('Waiter.StaffAddDialog.Field.Pin.Label')"
                              :prepend-icon="$t('Waiter.StaffAddDialog.Field.Pin.Icon')"
                              :rules="validationRules.deliveryStaffForm.Password"
                              :type="!pinVisible ? 'password' : 'text'"
                              autocomplete="one-time-code"
                              class="pr-2"
                              maxlength="4"
                              required
                              @click:append="(pinVisible = !pinVisible)"
                            >
                              <template #append-outer>
                                <v-btn
                                  :disabled="!editDeliveryStaff.User.Password"
                                  class="ma-0"
                                  icon
                                  small
                                  @click="copyToClipboard(editDeliveryStaff.User.Password)"
                                >
                                  <v-icon small>
                                    far fa-copy
                                  </v-icon>
                                </v-btn>
                              </template>
                            </v-text-field>
                          </v-col>

                          <template v-if="!deliveryStaffToEdit || (deliveryStaffToEdit && !editDeliveryStaff?.User?.EftPos)">
                            <v-col cols="12">
                              <v-switch
                                v-model="editDeliveryStaff.User.Active"
                                :disabled="!userCanDelete"
                                :label="editDeliveryStaff.User.Active ? $t('Common.Button.Toggle.ActiveM') : $t('Common.Button.Toggle.InactiveM')"
                                class="mt-4 mr-8 d-inline-flex"
                                color="success"
                                inset
                              >
                                <template #prepend>
                                  <span class="mt-1 grey--text">
                                    {{ $t('Waiter.StaffAddDialog.Field.Status.Title') }}
                                  </span>
                                </template>
                              </v-switch>

                              <v-switch
                                v-if="userIsSuperAdmin && !hasEftPosDeliveryStaff"
                                v-model="editDeliveryStaff.User.EftPos"
                                :disabled="!userCanDelete"
                                :label="editDeliveryStaff.User.Active ? $t('Common.Button.Toggle.Active') : $t('Common.Button.Toggle.Inactive')"
                                class="mt-4 mr-8 d-inline-flex"
                                color="purple"
                                inset
                                @change="onEftPosEnabled"
                              >
                                <template #prepend>
                                  <span class="mt-1 grey--text">
                                    EFT/POS
                                  </span>
                                </template>
                              </v-switch>
                            </v-col>
                          </template>
                        </v-row>

                        <template v-if="!deliveryStaffToEdit || (deliveryStaffToEdit && (!editDeliveryStaff?.User?.EftPos || userIsSuperAdmin))">
                          <v-row dense>
                            <v-col cols="12">
                              <v-card flat>
                                <v-card-title :class="[isDarkMode ? 'grey darken-4' : 'grey lighten-4']">
                                  <v-icon class="pr-3">
                                    {{ $t('Settings.Settings.Toolbar.Icon') }}
                                  </v-icon>
                                  <h5 class="text-body-2 font-weight-bold">
                                    {{ $t('Delivery.DeliveryUser.Settings.Title') }}
                                  </h5>

                                  <v-spacer />

                                  <v-btn
                                    v-if="staffHasUser && userIsSuperAdmin"
                                    class="px-2"
                                    color="light-blue"
                                    dark
                                    depressed
                                    small
                                    @click="onDeliveryUserReset"
                                  >
                                    RESET DEFAULTS
                                  </v-btn>
                                </v-card-title>

                                <v-divider />

                                <v-card-text>
                                  <v-container
                                    class="pa-0"
                                    fluid
                                  >
                                    <v-row
                                      align="center"
                                      dense
                                    >
                                      <v-col>
                                        <h5>{{ $t('Delivery.DeliveryUser.Settings.CanOpenShift.Title') }}</h5>
                                        <span class="text-caption">{{ $t('Delivery.DeliveryUser.Settings.CanOpenShift.Subtitle') }}</span>
                                      </v-col>

                                      <v-col class="shrink">
                                        <v-switch
                                          v-model="editDeliveryStaff.User.Settings.DeliveryStaff.Permissions.CanOpenShift"
                                          :disabled="!userCanManage"
                                          :label="editDeliveryStaff.User.Settings.DeliveryStaff.Permissions.CanOpenShift ? $t('Common.Button.Toggle.Active') : $t('Common.Button.Toggle.Inactive')"
                                          class="mt-0 pt-0"
                                          color="success"
                                          hide-details
                                          inset
                                        />
                                      </v-col>

                                      <v-col cols="12">
                                        <v-divider class="" />
                                      </v-col>
                                    </v-row>

                                    <v-row
                                      align="center"
                                      dense
                                    >
                                      <v-col>
                                        <h5>{{ $t('Delivery.DeliveryUser.Settings.CanCloseShift.Title') }}</h5>
                                        <span class="text-caption">{{ $t('Delivery.DeliveryUser.Settings.CanCloseShift.Subtitle') }}</span>
                                      </v-col>

                                      <v-col class="shrink">
                                        <v-switch
                                          v-model="editDeliveryStaff.User.Settings.DeliveryStaff.Permissions.CanCloseShift"
                                          :disabled="!userCanManage"
                                          :label="editDeliveryStaff.User.Settings.DeliveryStaff.Permissions.CanCloseShift ? $t('Common.Button.Toggle.Active') : $t('Common.Button.Toggle.Inactive')"
                                          class="mt-0 pt-0"
                                          color="success"
                                          hide-details
                                          inset
                                        />
                                      </v-col>

                                      <v-col cols="12">
                                        <v-divider class="" />
                                      </v-col>
                                    </v-row>

                                    <v-row
                                      align="center"
                                      dense
                                    >
                                      <v-col>
                                        <h5>{{ $t('Delivery.DeliveryUser.Settings.CanAddOrder.Title') }}</h5>
                                        <span class="text-caption">{{ $t('Delivery.DeliveryUser.Settings.CanAddOrder.Subtitle') }}</span>
                                      </v-col>

                                      <v-col class="shrink">
                                        <v-switch
                                          v-model="editDeliveryStaff.User.Settings.DeliveryStaff.Permissions.CanAddOrder"
                                          :disabled="!userCanManage"
                                          :label="editDeliveryStaff.User.Settings.DeliveryStaff.Permissions.CanAddOrder ? $t('Common.Button.Toggle.Active') : $t('Common.Button.Toggle.Inactive')"
                                          class="mt-0 pt-0"
                                          color="success"
                                          hide-details
                                          inset
                                        />
                                      </v-col>

                                      <v-col cols="12">
                                        <v-divider class="" />
                                      </v-col>
                                    </v-row>

                                    <v-row
                                      align="center"
                                      dense
                                    >
                                      <v-col>
                                        <h5>{{ $t('Delivery.DeliveryUser.Settings.CanDeleteOrder.Title') }}</h5>
                                        <span class="text-caption">{{ $t('Delivery.DeliveryUser.Settings.CanDeleteOrder.Subtitle') }}</span>
                                      </v-col>

                                      <v-col class="shrink">
                                        <v-switch
                                          v-model="editDeliveryStaff.User.Settings.DeliveryStaff.Permissions.CanDeleteOrder"
                                          :disabled="!userCanManage"
                                          :label="editDeliveryStaff.User.Settings.DeliveryStaff.Permissions.CanDeleteOrder ? $t('Common.Button.Toggle.Active') : $t('Common.Button.Toggle.Inactive')"
                                          class="mt-0 pt-0"
                                          color="success"
                                          hide-details
                                          inset
                                        />
                                      </v-col>

                                      <v-col cols="12">
                                        <v-divider class="" />
                                      </v-col>
                                    </v-row>

                                    <v-row
                                      align="center"
                                      dense
                                    >
                                      <v-col>
                                        <h5>{{ $t('Delivery.DeliveryUser.Settings.CanTransactionSale.Title') }}</h5>
                                        <span class="text-caption">{{ $t('Delivery.DeliveryUser.Settings.CanTransactionSale.Subtitle') }}</span>
                                      </v-col>

                                      <v-col class="shrink">
                                        <v-switch
                                          v-model="editDeliveryStaff.User.Settings.DeliveryStaff.Permissions.CanTransactionSale"
                                          :disabled="!userCanManage"
                                          :label="editDeliveryStaff.User.Settings.DeliveryStaff.Permissions.CanTransactionSale ? $t('Common.Button.Toggle.Active') : $t('Common.Button.Toggle.Inactive')"
                                          class="mt-0 pt-0"
                                          color="success"
                                          hide-details
                                          inset
                                        />
                                      </v-col>

                                      <v-col cols="12">
                                        <v-divider class="" />
                                      </v-col>
                                    </v-row>

                                    <v-row
                                      align="center"
                                      dense
                                    >
                                      <v-col>
                                        <h5>{{ $t('Delivery.DeliveryUser.Settings.CanGenerateDocument.Title') }}</h5>
                                        <span class="text-caption">{{ $t('Delivery.DeliveryUser.Settings.CanGenerateDocument.Subtitle') }}</span>
                                      </v-col>

                                      <v-col class="shrink">
                                        <v-switch
                                          v-model="editDeliveryStaff.User.Settings.DeliveryStaff.Permissions.CanGenerateDocument"
                                          :disabled="!userCanManage"
                                          :label="editDeliveryStaff.User.Settings.DeliveryStaff.Permissions.CanGenerateDocument ? $t('Common.Button.Toggle.Active') : $t('Common.Button.Toggle.Inactive')"
                                          class="mt-0 pt-0"
                                          color="success"
                                          hide-details
                                          inset
                                        />
                                      </v-col>

                                      <v-col cols="12">
                                        <v-divider class="" />
                                      </v-col>
                                    </v-row>

                                    <v-row
                                      align="center"
                                      dense
                                    >
                                      <v-col>
                                        <h5>{{ $t('Delivery.DeliveryUser.Settings.IsGenerateDocumentRequired.Title') }}</h5>
                                        <span class="text-caption">{{ $t('Delivery.DeliveryUser.Settings.IsGenerateDocumentRequired.Subtitle') }}</span>
                                      </v-col>

                                      <v-col class="shrink">
                                        <v-switch
                                          v-model="editDeliveryStaff.User.Settings.DeliveryStaff.Permissions.IsGenerateDocumentRequired"
                                          :disabled="!userCanManage"
                                          :label="editDeliveryStaff.User.Settings.DeliveryStaff.Permissions.IsGenerateDocumentRequired ? $t('Common.Button.Toggle.Active') : $t('Common.Button.Toggle.Inactive')"
                                          class="mt-0 pt-0"
                                          color="success"
                                          hide-details
                                          inset
                                        />
                                      </v-col>

                                      <v-col cols="12">
                                        <v-divider class="" />
                                      </v-col>
                                    </v-row>

                                    <v-row
                                      align="center"
                                      dense
                                    >
                                      <v-col>
                                        <h5>{{ $t('Delivery.DeliveryUser.Settings.CanPrintDocument.Title') }}</h5>
                                        <span class="text-caption">{{ $t('Delivery.DeliveryUser.Settings.CanPrintDocument.Subtitle') }}</span>
                                      </v-col>

                                      <v-col class="shrink">
                                        <v-switch
                                          v-model="editDeliveryStaff.User.Settings.DeliveryStaff.Permissions.CanPrintDocument"
                                          :disabled="!userCanManage"
                                          :label="editDeliveryStaff.User.Settings.DeliveryStaff.Permissions.CanPrintDocument ? $t('Common.Button.Toggle.Active') : $t('Common.Button.Toggle.Inactive')"
                                          class="mt-0 pt-0"
                                          color="success"
                                          hide-details
                                          inset
                                        />
                                      </v-col>

                                      <v-col cols="12">
                                        <v-divider class="" />
                                      </v-col>

                                      <v-col>
                                        <h5>{{ $t('Staff.StaffAddDialog.Settings.ActivePrintMachineGroups.Title') }}</h5>
                                        <span class="text-caption">{{ $t('Staff.StaffAddDialog.Settings.ActivePrintMachineGroups.Subtitle') }}</span>
                                      </v-col>
                                      <v-col class="shrink">
                                        <v-switch
                                          v-model="editDeliveryStaff.User.Settings.AllUsers.ActivePrintMachineGroups.Active"
                                          :disabled="!userCanManage"
                                          :label="editDeliveryStaff.User.Settings.AllUsers.ActivePrintMachineGroups.Active ? $t('Common.Button.Toggle.Active') : $t('Common.Button.Toggle.Inactive')"
                                          class="mt-0 pt-0"
                                          color="success"
                                          hide-details
                                          inset
                                        />
                                      </v-col>

                                      <v-col
                                        v-if="editDeliveryStaff.User.Settings.AllUsers.ActivePrintMachineGroups.Active"
                                        cols="12"
                                      >
                                        <v-select
                                          v-model="editDeliveryStaff.User.Settings.AllUsers.ActivePrintMachineGroups.Items"
                                          :items="appMachineGroups"
                                          :placeholder="$t('Restaurant.TableAreaAddDialog.Field.MachinesOrders.Label')"
                                          class="mt-2"
                                          clearable
                                          dense
                                          hide-details
                                          item-text="Name"
                                          item-value="Id"
                                          multiple
                                          outlined
                                        >
                                          <template #selection="{item, parent, selected}">
                                            <v-chip
                                              :value="selected"
                                              label
                                              small
                                            >
                                              <v-icon
                                                left
                                                small
                                              >
                                                mdi-desktop-classic
                                              </v-icon>

                                              <span class="pr-2">
                                                {{ item.Name }}
                                              </span>

                                              <v-icon
                                                small
                                                @click="parent.selectItem(item)"
                                              >
                                                close
                                              </v-icon>
                                            </v-chip>
                                          </template>
                                        </v-select>
                                      </v-col>

                                      <v-col cols="12">
                                        <v-divider class="" />
                                      </v-col>
                                    </v-row>

                                    <v-row
                                      v-if="userIsSuperAdmin"
                                      align="center"
                                    >
                                      <v-col>
                                        <h5>{{ $t('Delivery.DeliveryUser.Settings.CanTransactionRefund.Title') }}</h5>
                                        <span class="text-caption">{{ $t('Delivery.DeliveryUser.Settings.CanTransactionRefund.Subtitle') }}</span>
                                      </v-col>

                                      <v-col class="shrink">
                                        <v-switch
                                          v-model="editDeliveryStaff.User.Settings.DeliveryStaff.Permissions.CanTransactionRefund"
                                          :disabled="!userCanManage"
                                          :label="editDeliveryStaff.User.Settings.DeliveryStaff.Permissions.CanTransactionRefund ? $t('Common.Button.Toggle.Active') : $t('Common.Button.Toggle.Inactive')"
                                          class="mt-0 pt-0"
                                          color="success"
                                          hide-details
                                          inset
                                        />
                                      </v-col>

                                      <v-col cols="12">
                                        <v-divider class="" />
                                      </v-col>
                                    </v-row>

                                    <v-row
                                      align="center"
                                      dense
                                    >
                                      <v-col>
                                        <h5>{{ $t('Delivery.DeliveryUser.Settings.TrackLocation.Title') }}</h5>
                                        <span class="text-caption">{{ $t('Delivery.DeliveryUser.Settings.TrackLocation.Subtitle') }}</span>
                                      </v-col>

                                      <v-col class="shrink">
                                        <v-switch
                                          v-model="editDeliveryStaff.User.Settings.DeliveryStaff.TrackLocation"
                                          :disabled="!userCanManage"
                                          :label="editDeliveryStaff.User.Settings.DeliveryStaff.TrackLocation ? $t('Common.Button.Toggle.Active') : $t('Common.Button.Toggle.Inactive')"
                                          class="mt-0 pt-0"
                                          color="success"
                                          hide-details
                                          inset
                                        />
                                      </v-col>

                                      <v-col cols="12">
                                        <v-divider class="" />
                                      </v-col>
                                    </v-row>
                                  </v-container>
                                </v-card-text>
                              </v-card>
                            </v-col>
                          </v-row>

                          <v-row dense>
                            <v-col cols="12">
                              <v-card flat>
                                <v-card-title :class="[isDarkMode ? 'grey darken-4' : 'grey lighten-4']">
                                  <v-icon class="pr-3">
                                    lock
                                  </v-icon>
                                  <h5 class="text-body-2 font-weight-bold">
                                    {{ $t('Settings.PinManager.Enabled.Title') }}
                                  </h5>
                                </v-card-title>

                                <v-divider />

                                <v-card-text>
                                  <v-container
                                    class="pa-0"
                                    fluid
                                  >
                                    <v-row
                                      align="center"
                                      dense
                                    >
                                      <v-col>
                                        <h5>{{ $t('Settings.Tables.Orders.Permission.ExcludeFromPin.Title') }}</h5>
                                        <span class="text-caption">{{ $t('Settings.Tables.Orders.Permission.ExcludeFromPin.Subtitle') }}</span>
                                      </v-col>

                                      <v-col class="shrink">
                                        <v-switch
                                          v-model="editDeliveryStaff.User.Settings.ExcludeFromPin"
                                          :disabled="!userCanManage"
                                          :label="editDeliveryStaff.User.Settings.ExcludeFromPin ? $t('Common.Button.Toggle.Active') : $t('Common.Button.Toggle.Inactive')"
                                          class="mt-0 pt-0"
                                          color="success"
                                          hide-details
                                          inset
                                        />
                                      </v-col>

                                      <v-col cols="12">
                                        <v-divider class="" />
                                      </v-col>
                                    </v-row>
                                  </v-container>
                                </v-card-text>
                              </v-card>
                            </v-col>

                            <v-col cols="12">
                              <v-card flat>
                                <v-card-title :class="[isDarkMode ? 'grey darken-4' : 'grey lighten-4']">
                                  <v-icon class="pr-3">
                                    lock
                                  </v-icon>
                                  <h5 class="text-body-2 font-weight-bold">
                                    {{ $t('Common.Button.Transactions') }}
                                  </h5>
                                </v-card-title>

                                <v-divider />

                                <v-card-text>
                                  <v-container
                                    class="pa-0"
                                    fluid
                                  >
                                    <v-row
                                      align="center"
                                      dense
                                    >
                                      <v-col>
                                        <h5>{{ $t('Staff.StaffAddDialog.Settings.PosTransactionManualAccept.Title') }}</h5>
                                        <span class="text-caption">{{ $t('Staff.StaffAddDialog.Settings.PosTransactionManualAccept.Subtitle') }}</span>
                                      </v-col>
                                      <v-col class="shrink">
                                        <v-switch
                                          v-model="editDeliveryStaff.User.Settings.AllUsers.Permissions.CanTransactionManualAccept"
                                          :disabled="!userCanManage"
                                          :label="editDeliveryStaff.User.Settings.AllUsers.Permissions.CanTransactionManualAccept ? $t('Common.Button.Toggle.Active') : $t('Common.Button.Toggle.Inactive')"
                                          class="mt-0 pt-0"
                                          color="success"
                                          hide-details
                                          inset
                                        />
                                      </v-col>

                                      <template v-if="editDeliveryStaff.User.Settings.AllUsers.Permissions.CanTransactionManualAccept">
                                        <v-col cols="12" />

                                        <v-col>
                                          <h5>{{ $t('Staff.StaffAddDialog.Settings.PosTransactionManualAcceptAllStates.Title') }}</h5>
                                          <span class="text-caption">{{ $t('Staff.StaffAddDialog.Settings.PosTransactionManualAcceptAllStates.Subtitle') }}</span>
                                        </v-col>
                                        <v-col class="shrink">
                                          <v-switch
                                            v-model="editDeliveryStaff.User.Settings.AllUsers.Permissions.CanTransactionManualAcceptAllStates"
                                            :disabled="!userCanManage"
                                            :label="editDeliveryStaff.User.Settings.AllUsers.Permissions.CanTransactionManualAcceptAllStates ? $t('Common.Button.Toggle.Active') : $t('Common.Button.Toggle.Inactive')"
                                            class="mt-0 pt-0"
                                            color="orange"
                                            hide-details
                                            inset
                                          />
                                        </v-col>
                                      </template>

                                      <v-col cols="12">
                                        <v-divider class="" />
                                      </v-col>

                                      <v-col>
                                        <h5>{{ $t('Staff.StaffAddDialog.Settings.ActivePaymentTypes.Title') }}</h5>
                                        <span class="text-caption">{{ $t('Staff.StaffAddDialog.Settings.ActivePaymentTypes.Subtitle') }}</span>
                                      </v-col>
                                      <v-col class="shrink">
                                        <v-switch
                                          v-model="editDeliveryStaff.User.Settings.AllUsers.ActivePaymentTypes.Active"
                                          :disabled="!userCanManage"
                                          :label="editDeliveryStaff.User.Settings.AllUsers.ActivePaymentTypes.Active ? $t('Common.Button.Toggle.Active') : $t('Common.Button.Toggle.Inactive')"
                                          class="mt-0 pt-0"
                                          color="success"
                                          hide-details
                                          inset
                                        />
                                      </v-col>

                                      <v-col
                                        v-if="editDeliveryStaff.User.Settings.AllUsers.ActivePaymentTypes.Active"
                                        cols="12"
                                      >
                                        <v-select
                                          v-model="editDeliveryStaff.User.Settings.AllUsers.ActivePaymentTypes.Items"
                                          :items="paymentTypesForUser"
                                          :placeholder="$t('Staff.StaffAddDialog.Settings.ActivePaymentTypes.Title')"
                                          class="mt-2"
                                          clearable
                                          dense
                                          hide-details
                                          item-text="name"
                                          item-value="code"
                                          multiple
                                          outlined
                                        >
                                          <template #selection="{item}">
                                            <v-chip
                                              :color="`${item.color1}`"
                                              class="px-1"
                                              dark
                                              label
                                              small
                                            >
                                              <v-icon
                                                class="mr-1"
                                                small
                                              >
                                                {{ item.icon }}
                                              </v-icon>
                                              <span>{{ item.name }}</span>
                                            </v-chip>
                                          </template>

                                          <template #item="data">
                                            <v-list-item-avatar
                                              :color="`${data.item.color1}`"
                                              :size="28"
                                              dark
                                            >
                                              <v-icon
                                                color="white"
                                                small
                                              >
                                                {{ data.item.icon }}
                                              </v-icon>
                                            </v-list-item-avatar>

                                            <v-list-item-content>
                                              <v-list-item-title>{{ data.item.name }}</v-list-item-title>
                                            </v-list-item-content>
                                          </template>
                                        </v-select>
                                      </v-col>

                                      <v-col cols="12">
                                        <v-divider class="" />
                                      </v-col>

                                      <v-col>
                                        <h5>{{ $t('Staff.StaffAddDialog.Settings.ActiveEftPos.Title') }}</h5>
                                        <span class="text-caption">{{ $t('Staff.StaffAddDialog.Settings.ActiveEftPos.Subtitle') }}</span>
                                      </v-col>
                                      <v-col class="shrink">
                                        <v-switch
                                          v-model="editDeliveryStaff.User.Settings.AllUsers.ActiveEftPos.Active"
                                          :disabled="!userCanManage"
                                          :label="editDeliveryStaff.User.Settings.AllUsers.ActiveEftPos.Active ? $t('Common.Button.Toggle.Active') : $t('Common.Button.Toggle.Inactive')"
                                          class="mt-0 pt-0"
                                          color="success"
                                          hide-details
                                          inset
                                        />
                                      </v-col>

                                      <v-col
                                        v-if="editDeliveryStaff.User.Settings.AllUsers.ActiveEftPos.Active"
                                        cols="12"
                                      >
                                        <v-select
                                          v-model="editDeliveryStaff.User.Settings.AllUsers.ActiveEftPos.Items"
                                          :items="eftPosForUser"
                                          :placeholder="$t('Staff.StaffAddDialog.Settings.ActiveEftPos.Title')"
                                          class="mt-2"
                                          clearable
                                          dense
                                          hide-details
                                          item-text="Nickname"
                                          item-value="Id"
                                          multiple
                                          outlined
                                        >
                                          <template #selection="{item}">
                                            <v-chip
                                              class="px-1"
                                              dark
                                              label
                                              small
                                            >
                                              <v-icon
                                                class="mr-1"
                                                small
                                              >
                                                mdi-credit-card-wireless
                                              </v-icon>

                                              <span>{{ item.Nickname }}</span>
                                            </v-chip>
                                          </template>

                                          <template #item="data">
                                            <v-list-item-avatar
                                              :size="28"
                                              dark
                                            >
                                              <v-icon
                                                color="green"
                                                small
                                              >
                                                mdi-credit-card-wireless
                                              </v-icon>
                                            </v-list-item-avatar>

                                            <v-list-item-content>
                                              <v-list-item-title>
                                                {{ data.item.Nickname }}
                                              </v-list-item-title>

                                              <v-list-item-subtitle class="text-caption">
                                                {{ getVendorAcquirerTextById(data.item.PosVendorId, data.item.AcquirerCode) }}
                                              </v-list-item-subtitle>

                                              <v-list-item-subtitle class="text-caption">
                                                {{ data.item.Manufactor }} - {{ data.item.Model }}
                                              </v-list-item-subtitle>
                                            </v-list-item-content>
                                          </template>
                                        </v-select>
                                      </v-col>

                                      <v-col cols="12">
                                        <v-divider class="" />
                                      </v-col>
                                    </v-row>
                                  </v-container>
                                </v-card-text>
                              </v-card>
                            </v-col>
                          </v-row>
                        </template>
                      </v-container>
                    </v-card-text>
                  </v-card>
                </v-col>
              </v-row>
            </v-container>
          </v-form>
        </v-card-text>

        <v-divider />

        <v-card-actions class="pa-4">
          <v-spacer />

          <v-btn
            class="light-blue--text text--darken-1"
            outlined
            @click="onCancelButtonClick"
          >
            {{ $t('Common.Button.Cancel') }}
          </v-btn>

          <v-btn
            :disabled="isLoading"
            :loading="isLoading"
            class="green white--text elevation-0"
            @click="onSaveButtonClick"
          >
            {{ $t('Common.Button.Save') }}
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </v-row>
</template>

<script>

import {clone, mergeDeep} from '@/lib/helper/helper'
import AppData from '@/mixins/appdata'
import Auth from '@/mixins/auth'
import DefaultUserSettings from '@/components/staff/DefaultUserSettings.js'
import PaymentType from '@/mixins/orders/paymentType.js'
import TerminalsCommon from '@/mixins/payments/terminalsCommon.js'

export default {
  components: {},
  mixins    : [AppData, Auth, PaymentType, TerminalsCommon],
  props     : {
    visible: {
      type   : Boolean,
      default: false
    },
    hasEftPosDeliveryStaff: {
      type   : Boolean,
      default: false
    },
    deliveryStaffToEdit: {
      type   : Object,
      default: undefined
    }
  },
  data() {
    return {
      isLoading             : false,
      deliveryStaffFormValid: false,
      pinVisible            : false,
      staffHasUser          : false,
      editDeliveryStaff     : null,
      tmpDeliveryStaff      : {
        Id       : null,
        Firstname: '',
        Lastname : '',
        Phone    : '',
        Email    : '',
        Notes    : '',
        User     : null
      },
      tmpUserData    : null,
      defaultUserData: {
        StaffId : null,
        UserId  : null,
        Username: '',
        Password: '',
        Active  : true,
        EftPos  : false,
        Settings: clone(DefaultUserSettings)
      },
      validationRules: {
        deliveryStaffForm: {
          Firstname: [
            (v) => v && v.length >= 2 && v.length <= 32 || this.$t('Delivery.DeliveryStaffAddDialog.Field.Firstname.Error.Between')
          ],
          Lastname: [
            (v) => !v || (v.length >= 0 && v.length <= 32) || this.$t('Delivery.DeliveryStaffAddDialog.Field.Lastname.Error.Between')
          ],
          Phone: [
            (v) => !v || (v.length >= 8 && v.length <= 16 && !isNaN(v)) || this.$t('Delivery.DeliveryStaffAddDialog.Field.Telephone.Error.Between')
          ],
          Email: [
            (v) => !v || (v.length >= 0 && v.length <= 96) || this.$t('Delivery.DeliveryStaffAddDialog.Field.Email.Error.Between'),
            (v) => !v || (/^\w+([.\-\\+]?\w+)*@\w+([.-]?\w+)*(\.\w{2,10})+$/.test(v)) || this.$t('Delivery.DeliveryStaffAddDialog.Field.Email.Error.Valid')
          ],
          Password: [
            (v) => this.staffHasUser && v && v.length === 4 && !isNaN(v) || this.$t('Waiter.StaffAddDialog.Field.Pin.Error.FixedNumber')
          ]
        }
      }
    }
  },
  computed: {
    paymentTypesForUser() {
      return this.paymentTypesForDeliveryStaff
    },

    eftPosForUser() {
      const retVal = []
      this.appConfig?.POS_VENDORS?.forEach(vendor => {
        vendor?.Terminals?.forEach(terminal => {
          if (terminal.Active) retVal.push(terminal)
        })
      })
      return retVal
    },

    isVisible: {
      get() {
        return this.visible
      },
      set(val) {
        this.$emit('update:visible', val)
      }
    }
  },

  watch: {
    staffHasUser(newVal) {
      if (newVal) {
        this.editDeliveryStaff.User = clone(this.tmpUserData ? this.tmpUserData : this.defaultUserData)
      } else {
        this.editDeliveryStaff.User = null
      }
    },

    isVisible(newVal) {
      if (newVal) {
        this.tmpUserData = null
        this.pinVisible = false
        this.staffHasUser = false

        if (this.deliveryStaffToEdit) {
          this.editDeliveryStaff = clone(this.deliveryStaffToEdit)
        } else {
          this.editDeliveryStaff = clone(this.tmpDeliveryStaff)
        }

        if (this.editDeliveryStaff?.User) {
          this.tmpUserData = clone(this.editDeliveryStaff.User)
          this.staffHasUser = true
        }

        this.$nextTick(() => {
          if (this.$refs.deliveryStaffForm) this.$refs.deliveryStaffForm.resetValidation()
        })
      }
    }
  },

  created() {
    // console.log('1. created');
  },

  mounted() {
    // console.log('2. mounted');
    this.$bus.$on(window.SocketCommand.Staff.Delivery.Save, this.onSaveResult)
  },

  updated() {
    // console.log('3. updated');
  },

  beforeDestroy() {
    // console.log('4. destroyed');
    this.$bus.$off(window.SocketCommand.Staff.Delivery.Save, this.onSaveResult)
  },

  methods: {
    setDeliveryUserDefaults() {
      if (!this.staffHasUser) return

      this.$nextTick(() => {
        this.editDeliveryStaff.User.Active = true
        this.editDeliveryStaff.User.Settings.ExcludeFromPin = true
        this.editDeliveryStaff.User.Settings.AllUsers.Permissions.CanTransactionManualAccept = true
        this.editDeliveryStaff.User.Settings.DeliveryStaff = mergeDeep(this.editDeliveryStaff.User.Settings.DeliveryStaff, {
          TrackLocation: true,
          Permissions  : {
            CanOpenShift        : true,
            CanCloseShift       : true,
            CanAddOrder         : true,
            CanDeleteOrder      : false,
            CanTransactionSale  : true,
            CanTransactionRefund: false
          }
        })
      })
    },

    setDeliveryEftPosUserDefaults() {
      if (!this.editDeliveryStaff.User.EftPos) return

      this.$nextTick(() => {

        this.editDeliveryStaff.Firstname = 'EFT/POS'
        this.editDeliveryStaff.Lastname = ''
        this.editDeliveryStaff.Phone = ''
        this.editDeliveryStaff.Email = ''
        this.editDeliveryStaff.Notes = ''
        this.editDeliveryStaff.User.Active = true
        this.editDeliveryStaff.User.Settings.ExcludeFromPin = true
        this.editDeliveryStaff.User.Settings.AllUsers.Permissions.CanTransactionManualAccept = true
        this.editDeliveryStaff.User.Settings.DeliveryStaff = mergeDeep(this.editDeliveryStaff.User.Settings.DeliveryStaff, {
          TrackLocation: true,
          Permissions  : {
            CanOpenShift        : false,
            CanCloseShift       : false,
            CanAddOrder         : false,
            CanDeleteOrder      : false,
            CanTransactionSale  : true,
            CanTransactionRefund: false
          }
        })

      })
    },

    onDeliveryUserReset() {
      if (this.staffHasUser && !this.editDeliveryStaff.User.EftPos) this.setDeliveryUserDefaults()
      if (this.staffHasUser && this.editDeliveryStaff.User.EftPos) this.setDeliveryEftPosUserDefaults()
    },

    onUserEnabled() {
      if (!this.staffHasUser) return
      this.setDeliveryUserDefaults()
    },

    onEftPosEnabled() {
      if (this.editDeliveryStaff.User.EftPos) {
        this.setDeliveryEftPosUserDefaults()
      } else {
        this.setDeliveryUserDefaults()
      }
    },

    onSaveButtonClick() {
      this.$refs.deliveryStaffForm.validate()

      if (this.deliveryStaffFormValid) {
        this.isLoading = true
        window.callAS(window.SocketCommand.Staff.Delivery.Save, {staff: this.editDeliveryStaff})
      } else {
        this.$bus.$emit('app-show-notification', {
          body: this.$t('Common.Misc.Notification.FieldError'),
          type: 'error',
          icon: 'warning'
        })
      }
    },

    onSaveResult(data) {
      if (data && data.status === 'success') {
        this.onCancelButtonClick()
        if (Array.isArray(data.result) && data.result.length) {
          this.$bus.$emit(window.SocketCommand.Staff.Delivery.All, data)
          this.$set(window.Vue, 'deliveryStaffItems', data.result || [])
        }
      } else {
        if (data && data.message) {
          this.$bus.$emit('app-show-notification', {
            body: data.message,
            type: 'error',
            icon: 'warning'
          })
        } else {
          this.$bus.$emit('app-show-notification', {
            body: this.$t('Common.Misc.Notification.GenericError'),
            type: 'error',
            icon: 'warning'
          })
        }
      }
      this.isLoading = false
    },

    onCancelButtonClick() {
      if (this.$refs.deliveryStaffForm) this.$refs.deliveryStaffForm.resetValidation()
      this.isVisible = false
      this.isLoading = false
    }

  }
}
</script>
